// components/ErrorBoundary.js
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import './ErrorBoundary.css'

// ErrorBoundary needs to be a class component
// This is a React limitation - error boundaries must be classes
class ErrorBoundaryBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Call the onError callback from props
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      errorInfo: errorInfo
    });
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback({
        error: this.state.error,
        errorInfo: this.state.errorInfo,
        reset: this.reset
      });
    }

    return this.props.children;
  }
}

// A functional wrapper around the class component
const ErrorBoundary = ({ children }) => {
  const dispatch = useDispatch();
  const [key, setKey] = useState(0);

  const handleError = useCallback((error, errorInfo) => {
    // Dispatch error to Redux
    dispatch({
      type: 'ERROR_OCCURRED',
      payload: {
        message: error.message,
        componentStack: errorInfo.componentStack,
        stack: error.stack,
        actionType: 'REACT_ERROR'
      }
    });
  }, [dispatch]);

  const handleReload = useCallback(() => {
    // Acknowledge the error
    dispatch({ type: 'ERROR_ACKNOWLEDGED' });
    
    // Attempt to recover by refreshing the page
    window.location.reload();
  }, [dispatch]);

  const handleGoBack = useCallback(() => {
    // Acknowledge the error
    dispatch({ type: 'ERROR_ACKNOWLEDGED' });
    
    // Go back to previous page
    window.history.back();
  }, [dispatch]);

  const handleDismissError = useCallback((reset) => {
    // Just acknowledge the error without navigation
    dispatch({ type: 'ERROR_ACKNOWLEDGED' });
    
    // Reset the error boundary state
    reset();
    
    // Force re-render by changing the key
    setKey(prevKey => prevKey + 1);
  }, [dispatch]);

  // Custom error UI
  const renderFallback = useCallback(({ error, errorInfo, reset }) => {
    return (
      <div className="error-boundary-container">
        <div className="error-boundary-content">
          <h2>Something went wrong</h2>
          <p>We're sorry, but there was an error in the application.</p>
          
          {process.env.NODE_ENV !== 'production' && (
            <details style={{ whiteSpace: 'pre-wrap' }}>
              <summary>Error Details</summary>
              <p>{error && error.toString()}</p>
              <p>Component Stack:</p>
              <pre>{errorInfo && errorInfo.componentStack}</pre>
            </details>
          )}
          
          <div className="error-boundary-actions">
            <button onClick={handleReload} className="btn btn-primary cus-btn">
              Reload
            </button>
            <button onClick={handleGoBack} className="btn btn-secondary cus-btn cus-btn-back">
              Go Back
            </button>
            <button onClick={() => handleDismissError(reset)} className="btn btn-outline-secondary cus-btn">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }, [handleReload, handleGoBack, handleDismissError]);

  return (
    <ErrorBoundaryBase 
      key={key}
      onError={handleError} 
      fallback={renderFallback}
    >
      {children}
    </ErrorBoundaryBase>
  );
};

export default ErrorBoundary;
